import type { FileId } from "@inrev/common";
import { fileViewerControl } from "@portal/api/files";
import { type FC, type ReactNode, useMemo } from "react";
import { FloatingPanel } from "../../patterns/floating-panel";
import { PDFViewer } from "../../patterns/pdf-viewer";

const DEFAULT_PANEL_WIDTH = 620;

export type FilePreviewPanelProps = {
	fileId: FileId;
	title: ReactNode;
	subtitle?: ReactNode;
	/** One-based page the viewer opens on. */
	page?: number;
	placement?: "left" | "right";
	resizable?: boolean;
	width?: number;
	onClose: () => void;
	/** Root test id; the viewer gets `${testId}-viewer`. */
	testId?: string;
	closeTestId?: string;
	closeTrackingId?: string;
	/** Prefixes the viewer's control tracking ids. */
	viewerTrackingId?: string;
};

/**
 * An uploaded PDF in a non-modal panel, so the screen behind it stays workable while it is open.
 */
export const FilePreviewPanel: FC<FilePreviewPanelProps> = ({
	fileId,
	title,
	subtitle,
	page,
	placement,
	resizable,
	width = DEFAULT_PANEL_WIDTH,
	onClose,
	testId = "file-preview-panel",
	closeTestId = `${testId}-close`,
	closeTrackingId = closeTestId,
	viewerTrackingId = `${testId}-viewer`,
}) => {
	const control = useMemo(() => fileViewerControl(fileId), [fileId]);
	const options = useMemo(() => ({ fitToWidth: true, page }), [page]);

	return (
		<FloatingPanel
			className="h-[80vh]"
			closeTestId={closeTestId}
			closeTrackingId={closeTrackingId}
			onClose={onClose}
			placement={placement}
			resizable={resizable}
			testId={testId}
			width={width}
		>
			<FloatingPanel.Header>
				<div className="min-w-0 flex-1">
					<FloatingPanel.Title>{title}</FloatingPanel.Title>
					{subtitle !== undefined && <FloatingPanel.Subtitle>{subtitle}</FloatingPanel.Subtitle>}
				</div>
				<FloatingPanel.Actions />
			</FloatingPanel.Header>
			<FloatingPanel.Body>
				<PDFViewer
					className="h-full w-full"
					control={control}
					key={fileId}
					notFoundMessage="This file was deleted."
					options={options}
					testId={`${testId}-viewer`}
					trackingId={viewerTrackingId}
				/>
			</FloatingPanel.Body>
		</FloatingPanel>
	);
};
